import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Building, 
  Users, 
  Award, 
  Clock, 
  Phone,
  Mail,
  MapPin,
  Home,
  Wrench,
  HardHat,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";

// Import images
import ottawaConstruction from "@/assets/ottawa-construction.jpg";
import residentialConstruction from "@/assets/residential-construction.jpg";
import generalContractingProjects from "@/assets/general-contracting-projects.jpg";

const Ottawa = () => {
  const cityData = {
    name: "Ottawa",
    region: "National Capital Region",
    population: "1.0M",
    slug: "ottawa-general-contracting",
    intro: "Canada's capital city, Ottawa, has significant government, commercial, and residential construction opportunities. Blue Knight Contracting specializes in delivering secure, reliable projects across the region.",
    featured_services: [
      "Government Facility Construction",
      "Office Buildings",
      "Residential Development",
      "Heritage Renovations"
    ],
    nearby_areas: ["Gatineau", "Kanata", "Orleans", "Nepean"],
    cta: "Get started with Ottawa contracting services today."
  };

  const faqData = [
    {
      question: "Do you have security clearance for government projects in Ottawa?",
      answer: "Yes, our team has appropriate security clearances and experience working on government facilities and secure projects in Ottawa and the National Capital Region."
    },
    {
      question: "Can you handle heritage building renovations in Ottawa?",
      answer: "Absolutely. We specialize in heritage building renovations and restoration projects in Ottawa, working closely with heritage preservation requirements and regulations."
    },
    {
      question: "What areas around Ottawa do you serve?",
      answer: "We serve Ottawa and the National Capital Region including Gatineau, Kanata, Orleans, Nepean, and surrounding communities in Eastern Ontario."
    },
    {
      question: "Do you work on federal government buildings?",
      answer: "Yes, we have extensive experience with federal government construction projects, meeting all security and compliance requirements for government facilities."
    },
    {
      question: "Can you handle both English and French project requirements?",
      answer: "Yes, as the National Capital Region is bilingual, our team can handle projects with both English and French requirements and documentation."
    },
    {
      question: "What types of residential projects do you handle in Ottawa?",
      answer: "We handle all types of residential construction in Ottawa including custom homes, major renovations, heritage home restoration, and multi-unit residential developments."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Blue Knight Contracting Ottawa",
    "description": "Professional general contracting and construction services in Ottawa and the National Capital Region. Specializing in government facilities, heritage renovations, and residential development.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Ottawa",
      "addressRegion": "Ontario",
      "addressCountry": "Canada"
    },
    "telephone": "+1-647-760-8854",
    "url": "https://blueknightcontracting.ca/ottawa",
    "areaServed": {
      "@type": "City",
      "name": "Ottawa"
    },
    "serviceType": [
      "General Contracting Ottawa",
      "Government Facility Construction",
      "Heritage Renovations Ottawa",
      "Residential Development Ottawa",
      "Office Buildings Ottawa"
    ]
  };

  const constructionServices = [
    {
      title: "Government Facility Construction",
      description: "Secure construction services for federal and provincial government buildings in Ottawa.",
      icon: Building,
      features: [
        "Federal government facilities",
        "Security-cleared construction",
        "Compliance with regulations",
        "LEED-certified buildings",
        "Office fit-outs",
        "Facility upgrades"
      ]
    },
    {
      title: "Office Buildings",
      description: "Modern office construction and renovation services in Ottawa's business districts.",
      icon: Wrench,
      features: [
        "Corporate headquarters",
        "Professional office spaces",
        "Government office buildings",
        "Tenant improvements",
        "Modern workspace design",
        "Technology integration"
      ]
    },
    {
      title: "Residential Development",
      description: "Custom homes and residential developments throughout Ottawa and surrounding areas.",
      icon: Home,
      features: [
        "Custom home construction",
        "Residential subdivisions",
        "Multi-unit developments",
        "Major renovations",
        "Luxury homes",
        "Energy-efficient builds"
      ]
    },
    {
      title: "Heritage Renovations",
      description: "Specialized heritage building restoration and renovation services in Ottawa.",
      icon: HardHat,
      features: [
        "Heritage building restoration",
        "Historical preservation",
        "Character home renovations",
        "Compliance with heritage rules",
        "Sensitive modernization",
        "Architectural preservation"
      ]
    }
  ];

  return (
    <>
      <SchemaMarkup type="LocalBusiness" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Ottawa General Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  General Contracting & Construction in Ottawa
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  {cityData.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Ottawa Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>Population: {cityData.population}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <span>{cityData.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Security Cleared</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={ottawaConstruction} 
                  alt="Parliament Hill with nearby construction projects" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Services */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Ottawa Construction Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Professional Construction Services in Ottawa
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From government facilities to heritage renovations, we deliver secure and reliable construction services across the National Capital Region.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {constructionServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Services List */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialties</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Featured Ottawa Construction Services
              </h2>
              <p className="text-xl text-muted-foreground">
                Specialized construction services for Canada's capital city and the National Capital Region.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cityData.featured_services.map((service, index) => (
                <Card key={index} className="group hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardContent className="p-6 text-center">
                    <div className="p-3 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{service}</h3>
                    <p className="text-muted-foreground text-sm">
                      Professional {service.toLowerCase()} services in Ottawa and the NCR.
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Nearby Service Areas */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">NCR Service Areas</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Serving Ottawa and Surrounding Areas
              </h2>
              <p className="text-xl text-muted-foreground">
                Professional construction services throughout the National Capital Region.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-primary/5">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Ottawa</span>
                  </div>
                </Card>
                {cityData.nearby_areas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Ottawa Construction FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about construction services in Ottawa and the NCR.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready to Start Your Ottawa Construction Project?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                {cityData.cta} Get professional construction services from Ottawa's trusted general contractors.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">Ottawa & NCR</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Ottawa Construction Quote</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Ottawa Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Ottawa;